/**
 * human-like-dialog.tsx — Hộp "Cài đặt AI giống người thật" (theo mẫu): mọi giá trị tự nhập được (ô số có nút ▲▼),
 * mỗi mục có ⓘ giải thích + ví dụ, báo lỗi đỏ dưới ô, Huỷ / Xác nhận.
 *
 *   1. Phản hồi giống người thật (công tắc chính — tắt thì các mục dưới mờ đi)
 *   2. Độ trễ phản hồi của AI: [min] ~ [max] giây
 *   3. Phản hồi tách tin nhắn (khối xám): Số tin nhắn tách tối đa [n] · Khoảng cách gửi giữa các tin [min] ~ [max] giây
 *   4. Trả lời kỹ lưỡng: công tắc + câu chờ (≤ 1000 ký tự, *** tách nhiều câu)
 * Dữ liệu: settings.humanLike (xem bot-settings.ts); backend kiểm lại lần nữa.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, MessagesSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { InfoTip } from './settings-row'
import { HL_MAX_DELAY, HL_MAX_SPLIT, HL_THOROUGH_MAX, humanLikeFieldErrors, type HumanLike } from './bot-settings'

/** Ô số nguyên có nút ▲▼ (gõ tay được). */
export function NumberStepper({ value, onChange, min, max, disabled, ariaLabel, invalid }: {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  disabled?: boolean
  ariaLabel: string
  invalid?: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [text, setText] = useState(String(value))
  useEffect(() => { setText(String(value)) }, [value])
  const commit = (raw: string) => {
    setText(raw)
    if (raw.trim() === '') return
    const n = Number(raw)
    if (Number.isFinite(n)) onChange(Math.trunc(n))
  }
  const step = (d: number) => onChange(Math.min(max, Math.max(min, (Number.isFinite(value) ? value : min) + d)))
  return (
    <div className={cn('inline-flex h-9 items-stretch overflow-hidden rounded-md border bg-background', invalid && 'border-destructive', disabled && 'opacity-60')}>
      <input
        type="text" inputMode="numeric" aria-label={ariaLabel} disabled={disabled}
        className="w-14 bg-transparent px-2 text-center text-sm outline-none"
        value={text}
        onChange={(e) => commit(e.target.value.replace(/[^\d-]/g, ''))}
        onBlur={() => { if (text.trim() === '') setText(String(value)) }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); step(1) }
          if (e.key === 'ArrowDown') { e.preventDefault(); step(-1) }
        }}
      />
      <div className="flex flex-col border-l">
        <button type="button" className="flex h-1/2 w-6 items-center justify-center hover:bg-muted disabled:opacity-40" onClick={() => step(1)} disabled={disabled || value >= max} aria-label={t('humanLike.increase', { label: ariaLabel })}>
          <ChevronUp className="h-3 w-3" />
        </button>
        <button type="button" className="flex h-1/2 w-6 items-center justify-center border-t hover:bg-muted disabled:opacity-40" onClick={() => step(-1)} disabled={disabled || value <= min} aria-label={t('humanLike.decrease', { label: ariaLabel })}>
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

function Field({ title, info, error, children }: { title: string; info: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-sm font-medium">{title} <InfoTip text={info} /></p>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function RangeInput({ value, onChange, disabled, label, invalid }: {
  value: { min: number; max: number }
  onChange: (v: { min: number; max: number }) => void
  disabled?: boolean
  label: string
  invalid?: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <NumberStepper value={value.min} min={0} max={HL_MAX_DELAY} onChange={(min) => onChange({ ...value, min })} disabled={disabled} ariaLabel={t('humanLike.rangeFrom', { label })} invalid={invalid} />
      <span className="text-muted-foreground">~</span>
      <NumberStepper value={value.max} min={0} max={HL_MAX_DELAY} onChange={(max) => onChange({ ...value, max })} disabled={disabled} ariaLabel={t('humanLike.rangeTo', { label })} invalid={invalid} />
      <span className="text-muted-foreground">{t('humanLike.seconds')}</span>
    </div>
  )
}

/** Dữ liệu cũ: "Chờ một nhịp" đang tắt → hiện độ trễ 0 ~ 0 (không chờ). */
export function toDialogValue(h: HumanLike): HumanLike {
  return h.replyDelay.enabled ? h : { ...h, replyDelay: { enabled: true, min: 0, max: 0 } }
}

export function HumanLikeDialog({ open, onOpenChange, value, onSave, canEdit }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  value: HumanLike
  onSave: (v: HumanLike) => void
  canEdit: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [d, setD] = useState<HumanLike>(() => toDialogValue(value))
  const [tried, setTried] = useState(false)
  useEffect(() => { if (open) { setD(toDialogValue(value)); setTried(false) } }, [open, value])
  const errors = humanLikeFieldErrors(d)
  // Báo lỗi ngay khi đang gõ cho min > max; lỗi khác hiện sau khi bấm Xác nhận.
  const show = (k: keyof typeof errors) => (tried || k === 'replyDelay' || k === 'bubbleDelay') ? errors[k] : undefined
  const off = !d.enabled || !canEdit
  const save = () => {
    setTried(true)
    if (d.enabled && Object.keys(errors).length) return
    onSave({ ...d, replyDelay: { ...d.replyDelay, enabled: d.replyDelay.max > 0 } })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessagesSquare className="h-4 w-4 text-primary" /> {t('humanLike.title')}</DialogTitle>
          <DialogDescription>{t('humanLike.description')}</DialogDescription>
        </DialogHeader>

        <label className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2.5">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            {t('humanLike.mainLabel')}
            <InfoTip text={t('humanLike.mainInfo')} />
          </span>
          <Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} disabled={!canEdit} aria-label={t('humanLike.mainLabel')} />
        </label>

        <div className={cn('space-y-4', !d.enabled && 'pointer-events-none opacity-50')}>
          <Field
            title={t('humanLike.delayTitle')} error={show('replyDelay')}
            info={t('humanLike.delayInfo')}
          >
            <RangeInput value={d.replyDelay} onChange={(r) => setD({ ...d, replyDelay: { ...d.replyDelay, ...r } })} disabled={off} label={t('humanLike.delayLabel')} invalid={!!show('replyDelay')} />
          </Field>

          <div className="space-y-4 rounded-lg bg-muted/40 p-3">
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              {t('humanLike.splitTitle')}
              <InfoTip text={t('humanLike.splitInfo')} />
            </p>
            <Field
              title={t('humanLike.maxSplitTitle')} error={show('maxSplit')}
              info={t('humanLike.maxSplitInfo', { max: HL_MAX_SPLIT })}
            >
              <NumberStepper value={d.maxSplit} min={1} max={HL_MAX_SPLIT} onChange={(n) => setD({ ...d, maxSplit: n })} disabled={off} ariaLabel={t('humanLike.maxSplitTitle')} invalid={!!show('maxSplit')} />
            </Field>
            <Field
              title={t('humanLike.bubbleTitle')} error={show('bubbleDelay')}
              info={t('humanLike.bubbleInfo')}
            >
              <RangeInput value={d.bubbleDelay} onChange={(r) => setD({ ...d, bubbleDelay: r })} disabled={off || d.maxSplit <= 1} label={t('humanLike.bubbleLabel')} invalid={!!show('bubbleDelay')} />
            </Field>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                {t('humanLike.thoroughTitle')}
                <InfoTip text={t('humanLike.thoroughInfo')} />
              </p>
              <Switch checked={d.thorough.enabled} onCheckedChange={(v) => setD({ ...d, thorough: { ...d.thorough, enabled: v } })} disabled={off} aria-label={t('humanLike.thoroughTitle')} />
            </div>
            <textarea
              className={cn('min-h-[90px] w-full resize-y rounded-md border bg-transparent p-3 text-sm outline-none focus:ring-1 focus:ring-ring', show('thorough') && 'border-destructive')}
              maxLength={HL_THOROUGH_MAX + 200}
              value={d.thorough.phrases}
              onChange={(e) => setD({ ...d, thorough: { ...d.thorough, phrases: e.target.value } })}
              disabled={off || !d.thorough.enabled}
              placeholder={t('humanLike.thoroughPlaceholder')}
            />
            <div className="flex justify-between text-[11px]">
              <span className="text-destructive">{show('thorough') ?? ''}</span>
              <span className={d.thorough.phrases.length > HL_THOROUGH_MAX ? 'text-destructive' : 'text-muted-foreground'}>{d.thorough.phrases.length}/{HL_THOROUGH_MAX}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canEdit && <Button onClick={save}>{t('common:actions.confirm')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  )
}
